import { useState } from "react";
import { useLogoStore } from "../state/logoStore";
import { extractPaletteFromFile } from "../engine/colorExtractor";
import { importSvgAsIcon } from "../engine/svgImporter";
import { importPsd } from "../engine/psdImporter";
import { importProjectFile } from "../engine/projectImporter";

interface Props {
  /** Compact = small swatches, no labels. Used on the Start page. */
  compact?: boolean;
}

type Status = "idle" | "loading" | "ok" | "error";

export function ImageUploader({ compact = false }: Props) {
  const {
    referencePalette, referenceImageURL, setReference, clearReference,
    addCustomIcon, setIcon, pushHistory, setStyle, setTitle, setSlogan,
  } = useLogoStore();
  const [hover, setHover] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState<string | null>(null);

  async function handleFile(file: File) {
    setStatus("loading"); setMessage(null);
    try {
      const lower = file.name.toLowerCase();
      // Route by extension first, then fall back to MIME for plain images
      if (lower.endsWith(".psd")) {
        await importPsdFile(file);
      } else if (lower.endsWith(".svg")) {
        await importSvgFile(file);
      } else if (lower.endsWith(".json") || lower.endsWith(".logoman.json")) {
        await importJsonFile(file);
      } else if (file.type.startsWith("image/")) {
        await importReferenceImage(file);
      } else {
        throw new Error("Unsupported file type. Try PNG, JPG, SVG, PSD, or .logoman.json");
      }
      setStatus("ok");
    } catch (e) {
      setStatus("error");
      setMessage(e instanceof Error ? e.message : String(e));
    }
  }

  async function importReferenceImage(file: File) {
    if (referenceImageURL) URL.revokeObjectURL(referenceImageURL);
    const url = URL.createObjectURL(file);
    const colors = await extractPaletteFromFile(file, { count: 8 });
    if (colors.length === 0) {
      URL.revokeObjectURL(url);
      throw new Error("Couldn't extract colors from this image. Try a different one.");
    }
    setReference(colors, url);
    setMessage(`Extracted ${colors.length} colors from ${file.name}`);
  }

  async function importSvgFile(file: File) {
    const icon = await importSvgAsIcon(file);
    pushHistory();
    addCustomIcon({
      id: icon.id, name: icon.name, paths: icon.paths,
      vb: icon.viewBox, addedAt: new Date().toISOString(),
    });
    setIcon(icon.id);
    setMessage(`Custom icon "${icon.name}" added and selected`);
  }

  async function importJsonFile(file: File) {
    const proj = await importProjectFile(file);
    pushHistory();
    setTitle(proj.title); setSlogan(proj.slogan); setIcon(proj.iconId);
    setStyle({
      titleFont: proj.titleFont, sloganFont: proj.sloganFont,
      titleColor: proj.titleColor, sloganColor: proj.sloganColor,
      iconColor: proj.iconColor, background: proj.background, layout: proj.layout,
      titleSize: proj.titleSize, sloganSize: proj.sloganSize, iconScale: proj.iconScale,
    });
    setMessage(`Project "${proj.title}" imported`);
  }

  async function importPsdFile(file: File) {
    const psd = await importPsd(file);
    pushHistory();
    if (psd.title) setTitle(psd.title);
    if (psd.slogan) setSlogan(psd.slogan);
    if (psd.palette.length > 0) {
      if (referenceImageURL) URL.revokeObjectURL(referenceImageURL);
      setReference(psd.palette, psd.thumbnailURL);
      // Auto-apply: lightest as bg, darkest as text, most-saturated as icon
      const sortedByLight = [...psd.palette].sort((a, b) => b.lightness - a.lightness);
      const sortedBySat = [...psd.palette].sort((a, b) => b.saturation - a.saturation);
      setStyle({
        background: sortedByLight[0].hex,
        titleColor: sortedByLight[sortedByLight.length - 1].hex,
        sloganColor: sortedByLight[Math.floor(sortedByLight.length / 2)].hex,
        iconColor: sortedBySat[0]?.hex || sortedByLight[sortedByLight.length - 1].hex,
      });
    }
    setMessage(`Imported ${file.name}: ${psd.title ? `title "${psd.title}", ` : ""}${psd.palette.length} colors`);
  }

  const dropzoneText = referenceImageURL
    ? "Drop another file to re-extract / re-import"
    : "Drop a file here, or click to choose";

  return (
    <div className={`uploader ${compact ? "compact" : ""}`}>
      <label
        className={`dropzone ${hover ? "hover" : ""} ${status === "loading" ? "loading" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setHover(true); }}
        onDragLeave={() => setHover(false)}
        onDrop={(e) => {
          e.preventDefault();
          setHover(false);
          const f = e.dataTransfer.files?.[0];
          if (f) handleFile(f);
        }}
      >
        <input
          type="file"
          accept="image/*,.svg,.psd,.json"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
          }}
        />
        <div className="dropzone-inner">
          {status === "loading" ? "Reading file…" : dropzoneText}
          <div className="dropzone-hint">
            Image (palette extract) · SVG (custom icon) · PSD (Photoshop import) · .logoman.json (re-import)
          </div>
        </div>
      </label>

      {referenceImageURL && (
        <div className="ref-preview">
          <img src={referenceImageURL} alt="Reference" />
        </div>
      )}

      {status === "error" && message && <div className="error-note">{message}</div>}
      {status === "ok" && message && <div className="ok-note">{message}</div>}

      {referencePalette.length > 0 && (
        <div className="palette-readout">
          <div className="palette-readout-head">
            <span>Extracted palette</span>
            <button className="link-btn" onClick={clearReference}>Clear</button>
          </div>
          <div className={`palette-mini ${compact ? "compact" : ""}`}>
            {referencePalette.map((c) => (
              <div
                key={c.hex}
                className="palette-mini-swatch"
                style={{ background: c.hex }}
                title={c.hex.toUpperCase()}
              >
                {!compact && <span style={{ color: c.lightness > 0.55 ? "#0f172a" : "#fff" }}>{c.hex.toUpperCase()}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
